import React, { ChangeEvent, forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import styled from 'styled-components';
import ConsignmentEntry from 'data/ConsignmentEntry';
import Comment from 'data/Comment';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import Button from 'components/UI/Button';
import Input from 'components/UI/Input';
import Textarea from 'components/UI/Textarea';
import ConfirmationModal from 'components/UI/ConfimationModal';
import { useModalState } from 'hooks/useAddonModalState';
import Icon from 'view/Icon';
import { Background, BadgeColor, Border, Main, Shade, Text } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import { BorderRadius } from 'styles/themeBorderRadius';
import { TEXT_LABELS, UNIT_PRICE_MAX } from 'constants/order';
import { isVendorComment } from 'utils/comment';
import {
  SectionCard,
  SectionTitle,
  SectionSubtitle,
  TableWrapper,
  Table,
  HeaderCell,
  BodyCell,
  ProductImage,
  ProductName,
  SkuText,
  ActionCellContent,
  TotalLabel,
  TotalValue
} from './EditOrder.styles';
import { EDIT_ORDER_ACTIONS } from 'constants/product';

const TypeTag = styled.span<{ $variant?: 'default' | 'addition' | 'removal' | 'updated' }>`
  display: inline-block;
  border: 1px solid ${({ $variant }) => $variant === 'default' || !$variant ? Border.main : 'transparent'};
  border-radius: ${BorderRadius.s};
  background-color: ${({ $variant }) => $variant === 'addition'
    ? `${Main.success}1A`
    : $variant === 'removal'
      ? `${Main.error}1A`
      : $variant === 'updated'
        ? `${BadgeColor.blue}1A`
        : 'transparent'};
  color: ${({ $variant }) => $variant === 'addition'
    ? Main.success
    : $variant === 'removal'
      ? Main.error
      : $variant === 'updated'
        ? BadgeColor.blue
        : Text.secondary};
  font-size: 12px;
  line-height: 16px;
  padding: ${Space.spHalf} ${Space.spBase};
  white-space: nowrap;
`;

const StepperButton = styled.button<any>`
  align-items: center;
  background-color: ${Background.white};
  border: 1px solid ${Border.main};
  border-radius: ${({ $side }) => $side === 'left'
    ? `${BorderRadius.m} 0 0 ${BorderRadius.m}`
    : `0 ${BorderRadius.m} ${BorderRadius.m} 0`};
  color: ${Shade.shade450};
  cursor: pointer;
  display: flex;
  height: 32px;
  justify-content: center;
  width: 28px;
`;

const StepperValue = styled.div`
  align-items: center;
  border-bottom: 1px solid ${Border.main};
  border-top: 1px solid ${Border.main};
  display: flex;
  height: 32px;
  justify-content: center;
  min-width: 40px;
  padding: 0 ${Space.spHalf};
`;

const CommentField = styled.div`
  textarea {
    background-color: ${Background.white};
    padding: ${Space.spBase};

    ::placeholder {
      font-size: 14px;
      font-style: normal;
    }
  }
`;

interface ItemImage {
  path: string;
};

// A catalog item found via lookup and added to the order on the UI side only - it has no
// backing ConsignmentEntry, so it is never persisted along with the real order items.
export interface AddedCatalogItem {
  id: number;
  sku: string;
  /** ProductVariant code — sent to OFMP as productId when the item is added */
  code: string;
  materialNumber?: string;
  productName: string;
  basePrice: number;
  productImages: ItemImage[];
};

interface Props {
  consignmentEntries: ConsignmentEntry[];
  addedItems: AddedCatalogItem[];
  removedEntryIds: number[];
  currencyIso: string;
  currencySymbol: string;
  orderTotal: number;
  onRemoveRow: (id: number, isAddition: boolean) => void;
  onRestoreRow: (id: number) => void;
  /** Notifies the host whenever an existing row has on-screen edits (quantity / unit price /
   *  comment) that diverge from the persisted values - additions and removals are tracked
   *  by the host screen itself. */
  onDraftEditsChange?: (hasEdits: boolean) => void;
  /** Notifies the host whenever any row that would be submitted has an invalid unit price,
   *  so the host can block the apply action while the error is on screen. */
  onValidityChange?: (hasInvalidRows: boolean) => void;
};

// Snapshot of an added catalog item with whatever quantity/price/comment changes the user
// has made to it on screen since it was added.
export interface AddedItemWithChanges extends AddedCatalogItem {
  quantity: number;
  unitPrice: string;
  comment: string;
};

export interface EditOrderItemsTableHandle {
  getAddedItemsWithChanges: () => AddedItemWithChanges[];
  getExistingEntryEdits: () => {
    id: number;
    sku: string;
    code: string;
    materialNumber?: string;
    productName: string;
    basePrice: number;
    productImages: ItemImage[];
    quantity: number;
    unitPrice: string;
    comment: string;
  }[];
};

interface DraftEntry {
  quantity: number;
  unitPrice: string;
  comment: string;
};

interface RowItem {
  id: number;
  sku?: string;
  materialNumber?: string;
  productName: string;
  productImages: ItemImage[];
  quantity: number;
  basePrice: number;
  /** Persisted edit-session marker from the last apply — ADDED / CHANGED */
  modificationType?: string;
  isAddition: boolean;
  isRemoved: boolean;
  /** The comments already on the order line. The vendor's own ones seed the row's Comment draft, so this is
   *  needed even though nothing renders it directly. */
  comments?: Comment[];
};

const amountFormatter = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatAmount = (value: number) => amountFormatter.format(value || 0);

const toCommentDraft = (comments?: Comment[]): string =>
  Array.from(new Set((comments || [])
    .filter(isVendorComment)
    .map(comment => comment.comment)))
    .join('\n\n');

const toDrafts = (rows: RowItem[]): Record<number, DraftEntry> =>
  rows.reduce((drafts, row) => ({
    ...drafts,
    [row.id]: {
      quantity: row.quantity,
      unitPrice: (row.basePrice || 0).toFixed(2),
      comment: toCommentDraft(row.comments)
    }
  }), {});

const toRowItem = (entry: ConsignmentEntry, removedEntryIds: number[]): RowItem => ({
  id: entry.id,
  sku: entry.sku,
  materialNumber: entry.materialNumber,
  productName: entry.productName,
  productImages: entry.productImages,
  quantity: entry.quantity,
  basePrice: entry.basePrice,
  modificationType: entry.modificationType,
  isAddition: false,
  isRemoved: removedEntryIds.includes(entry.id),
  comments: entry.comments
});

const toAddedRowItem = (item: AddedCatalogItem): RowItem => ({
  id: item.id,
  sku: item.sku,
  materialNumber: item.materialNumber,
  productName: item.productName,
  productImages: item.productImages,
  quantity: 1,
  basePrice: item.basePrice,
  isAddition: true,
  isRemoved: false
});

export const getUnitPriceError = (value: string): string | undefined => {
  if (value.trim() === '') return TEXT_LABELS.UNIT_PRICE_REQUIRED;

  const numericValue = Number(value);
  if (Number.isNaN(numericValue) || numericValue < 0) return TEXT_LABELS.UNIT_PRICE_INVALID;
  if (numericValue === 0) return TEXT_LABELS.UNIT_PRICE_REQUIRED;
  if (numericValue > UNIT_PRICE_MAX) return TEXT_LABELS.UNIT_PRICE_MAX;

  return undefined;
};

export const isExistingRowEdited = (row: RowItem, draft?: DraftEntry): boolean => {
  if (row.isAddition || row.isRemoved || row.modificationType === EDIT_ORDER_ACTIONS.REMOVED) return false;
  if (!draft) return false;

  return draft.quantity !== row.quantity
    || draft.unitPrice.trim() === ''
    || Number(draft.unitPrice) !== Number(row.basePrice || 0)
    || draft.comment !== toCommentDraft(row.comments);
};

/**
 * "Items in this order" section of the Edit order screen. Existing order items can be
 * edited, removed (marked for removal, restorable before applying) or re-priced on
 * screen; changes are only persisted when the user applies them. Items added via the
 * catalog lookup are UI-only additions until applied.
 */
const EditOrderItemsTable = forwardRef<EditOrderItemsTableHandle, Props>(({ consignmentEntries, addedItems, removedEntryIds, currencyIso, currencySymbol, orderTotal, onRemoveRow, onRestoreRow, onDraftEditsChange, onValidityChange }, ref) => {
  const rows = useMemo(() => [
    ...consignmentEntries.map(entry => toRowItem(entry, removedEntryIds)),
    ...addedItems.map(toAddedRowItem)
  ], [consignmentEntries, addedItems, removedEntryIds]);

  const [drafts, setDrafts] = useState<Record<number, DraftEntry>>(() => toDrafts(rows));
  const [pendingRemoval, setPendingRemoval] = useState<{ id: number, isAddition: boolean } | null>(null);
  const { modalState, showModal, hideModal } = useModalState(['removeItem']);

  // Preserve any edits already made to existing rows - only seed defaults for rows that
  // don't have a draft yet, so adding a new catalog item doesn't reset the others.
  useEffect(() => {
    setDrafts(current => {
      const missingRows = rows.filter(row => !current[row.id]);
      if (missingRows.length === 0) return current;

      return { ...current, ...toDrafts(missingRows) };
    });
  }, [rows]);

  // Existing (non-removed) rows whose drafts diverge from the persisted values - the
  // host flags unsaved changes from this and each row shows an "Updated" tag.
  const editedRowIds = useMemo(() => new Set(
    rows.filter(row => isExistingRowEdited(row, drafts[row.id])).map(row => row.id)
  ), [rows, drafts]);

  const hasDraftEdits = editedRowIds.size > 0;

  useEffect(() => {
    onDraftEditsChange?.(hasDraftEdits);
  }, [hasDraftEdits, onDraftEditsChange]);

  // Any row that would go into the apply payload (edited existing rows and additions)
  // with a unit price that fails validation - removed rows are never submitted. The
  // persisted value is used when a row has no draft yet, matching what the imperative
  // getters would send, so a freshly added row isn't briefly reported as invalid.
  const hasInvalidUnitPrice = useMemo(() => rows.some(row => {
    if (row.isRemoved || row.modificationType === EDIT_ORDER_ACTIONS.REMOVED) return false;

    return !!getUnitPriceError(drafts[row.id]?.unitPrice ?? (row.basePrice || 0).toFixed(2));
  }), [rows, drafts]);

  useEffect(() => {
    onValidityChange?.(hasInvalidUnitPrice);
  }, [hasInvalidUnitPrice, onValidityChange]);

  useImperativeHandle(ref, () => ({
    getAddedItemsWithChanges: () => addedItems.map(item => {
      const draft = drafts[item.id];
      return {
        ...item,
        quantity: draft?.quantity ?? 1,
        unitPrice: draft?.unitPrice ?? item.basePrice.toFixed(2),
        comment: draft?.comment ?? ''
      };
    }),
    getExistingEntryEdits: () => consignmentEntries
      // REMOVED entries are display-only and never sent back on apply
      .filter(entry => !removedEntryIds.includes(entry.id) && entry.modificationType !== EDIT_ORDER_ACTIONS.REMOVED)
      .map(entry => {
        const draft = drafts[entry.id];
        return {
          id: entry.id,
          sku: entry.sku,
          code: entry.code,
          materialNumber: entry.materialNumber,
          productName: entry.productName,
          basePrice: entry.basePrice,
          productImages: entry.productImages,
          quantity: draft?.quantity ?? entry.quantity,
          unitPrice: draft?.unitPrice ?? entry.basePrice.toFixed(2),
          // Only a comment the seller actually changed is sent. The backend reads a
          // non-empty comment as "the user edited it" and rewrites the vendor's Comment
          // rows from it, so sending the seeded original back would flag the line
          // CHANGED to OFMP on an untouched row.
          comment: draft && draft.comment !== toCommentDraft(entry.comments) ? draft.comment : ''
        };
      })
  }), [addedItems, drafts, consignmentEntries, removedEntryIds]);

  const updateDraft = (rowId: number, changes: Partial<DraftEntry>) =>
    setDrafts(current => ({ ...current, [rowId]: { ...current[rowId], ...changes } }));

  return (
  <SectionCard>
    <SectionTitle>{`${TEXT_LABELS.ITEMS_IN_THIS_ORDER} (${rows.length})`}</SectionTitle>
    <SectionSubtitle>{TEXT_LABELS.ITEMS_IN_THIS_ORDER_SUBTITLE}</SectionSubtitle>
    <TableWrapper>
      <Table>
        <thead>
          <tr>
            <HeaderCell width="10%">{TEXT_LABELS.CHANGE}</HeaderCell>
            <HeaderCell width="19%">{TEXT_LABELS.PRODUCT}</HeaderCell>
            <HeaderCell width="11%">{TEXT_LABELS.ITEM_ID_SKU}</HeaderCell>
            <HeaderCell width="13%">{`${TEXT_LABELS.UNIT_PRICE} (${currencyIso})`}</HeaderCell>
            <HeaderCell width="10.5%" align="center">{TEXT_LABELS.QUANTITY}</HeaderCell>
            <HeaderCell width="9.5%" align="center">{`${TEXT_LABELS.SUBTOTAL} (${currencyIso})`}</HeaderCell>
            <HeaderCell width="19%">{TEXT_LABELS.VENDOR_COMMENT}</HeaderCell>
            <HeaderCell width="8%" align="center">{TEXT_LABELS.ACTION}</HeaderCell>
          </tr>
        </thead>
        <tbody>
        {
          rows.map(row => {
            const draft = drafts[row.id];
            const quantity = draft?.quantity ?? row.quantity;
            const unitPrice = Number(draft?.unitPrice ?? row.basePrice) || 0;
            const subTotal = quantity * unitPrice;
            const productImage = row.productImages && row.productImages.length > 0
              ? process.env.REACT_APP_MEDIA_URL! + row.productImages[0].path
              : '';
            const unitPriceError = getUnitPriceError(draft?.unitPrice ?? '');
            const isEdited = editedRowIds.has(row.id);
            // Persisted markers from the last apply in this session also flag the row —
            // an ADDED entry keeps the addition tag, a CHANGED entry the updated tag
            // (markers are never UNCHANGED, so untouched rows stay plain order items),
            // but a pending removal wins over the ADDED marker
            const isPersistedAddition = !row.isAddition && row.modificationType === EDIT_ORDER_ACTIONS.ADDED;
            const isPersistedUpdate = !row.isAddition && row.modificationType === EDIT_ORDER_ACTIONS.CHANGED;
            // Removal confirmed by an earlier apply in this session — the row stays
            // visible for reference but is no longer editable
            const isPersistedRemoved = !row.isAddition && row.modificationType === EDIT_ORDER_ACTIONS.REMOVED;
            const isRemoval = row.isRemoved || isPersistedRemoved;
            const tagVariant = isRemoval
              ? 'removal'
              : row.isAddition || isPersistedAddition
                ? 'addition'
                : isEdited || isPersistedUpdate
                  ? 'updated'
                  : 'default';
            const tagLabel = isRemoval
              ? TEXT_LABELS.REMOVAL_FROM_ORDER_TAG
              : row.isAddition || isPersistedAddition
                ? TEXT_LABELS.ADDITION_TO_ORDER_TAG
                : isEdited || isPersistedUpdate
                  ? TEXT_LABELS.UPDATED_ITEM_TAG
                  : TEXT_LABELS.ORDER_ITEM_TAG;

            return (
              <tr key={row.id}>
                <BodyCell>
                  <TypeTag $variant={tagVariant}>
                    {tagLabel}
                  </TypeTag>
                </BodyCell>
                <BodyCell>
                  <FlexboxContainer alignItems="center" gap={Space.spBasePlus}>
                    <ProductImage src={productImage} alt="" />
                    <ProductName>{row.productName}</ProductName>
                  </FlexboxContainer>
                </BodyCell>
                <BodyCell>
                  <SkuText>{row.materialNumber || row.sku}</SkuText>
                </BodyCell>
                <BodyCell>
                  <Input
                    id={`edit-order-unit-price-${row.id}`}
                    value={draft?.unitPrice ?? ''}
                    placeholder=""
                    textAlign="left"
                    isInvalid={!!unitPriceError}
                    errorMessage={unitPriceError}
                    disabled={isRemoval}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => updateDraft(row.id, { unitPrice: e.target.value })}
                  />
                </BodyCell>
                <BodyCell align="center">
                  <FlexboxContainer justifyContent="center">
                    <StepperButton
                      type="button"
                      $side="left"
                      disabled={isRemoval}
                      onClick={() => { if (!isRemoval) updateDraft(row.id, { quantity: Math.max(1, quantity - 1) }); }}
                    >
                      <Icon name="minus" size={14} />
                    </StepperButton>
                    <StepperValue className="base">{quantity}</StepperValue>
                    <StepperButton
                      type="button"
                      $side="right"
                      disabled={isRemoval}
                      onClick={() => { if (!isRemoval) updateDraft(row.id, { quantity: quantity + 1 }); }}
                    >
                      <Icon name="plus" size={14} />
                    </StepperButton>
                  </FlexboxContainer>
                </BodyCell>
                <BodyCell align="center">{`${currencySymbol}${formatAmount(subTotal)}`}</BodyCell>
                <BodyCell>
                  <CommentField>
                    <Textarea
                      id={`edit-order-comment-${row.id}`}
                      value={draft?.comment ?? ''}
                      placeholder={TEXT_LABELS.OPTIONAL_COMMENT}
                      rows={2}
                      disabled={isRemoval}
                      onChange={(value: string) => updateDraft(row.id, { comment: value })}
                    />
                  </CommentField>
                </BodyCell>
                <BodyCell align="center">
                  <ActionCellContent>
                    {row.isRemoved ? (
                      <Button
                        isGhost
                        color={Text.primary}
                        borderColor={Border.main}
                        onClick={() => onRestoreRow(row.id)}
                      >
                        {TEXT_LABELS.RESTORE}
                      </Button>
                    ) : isPersistedRemoved ? (
                      // Removal confirmed by an earlier apply — Restore flips the row back
                      // to a live row locally; the DB stays REMOVED until the next apply
                      <Button
                        isGhost
                        color={Text.primary}
                        borderColor={Border.main}
                        onClick={() => onRestoreRow(row.id)}
                      >
                        {TEXT_LABELS.RESTORE}
                      </Button>
                    ) : (
                      <Button
                        isGhost
                        theme="error"
                        color={Main.error}
                        borderColor={Main.error}
                        onClick={() => { setPendingRemoval({ id: row.id, isAddition: row.isAddition }); showModal('removeItem'); }}
                      >
                        {TEXT_LABELS.REMOVE}
                      </Button>
                    )}
                  </ActionCellContent>
                </BodyCell>
              </tr>
            );
          })
        }
        </tbody>
      </Table>
    </TableWrapper>
    <FlexboxContainer justifyContent="flex-end" alignItems="center" margin={`${Space.sp2x} 0 0`}>
      <TotalLabel>{`${TEXT_LABELS.ORDER_TOTAL} (${currencyIso})`}</TotalLabel>
      <TotalValue>{`${currencySymbol}${formatAmount(orderTotal)}`}</TotalValue>
    </FlexboxContainer>
    <ConfirmationModal
      show={modalState.removeItem}
      onClose={() => hideModal('removeItem')}
      onConfirm={() => {
        if (pendingRemoval) onRemoveRow(pendingRemoval.id, pendingRemoval.isAddition);
        hideModal('removeItem');
      }}
      title={TEXT_LABELS.REMOVE_ITEM_CONFIRM_TITLE}
      message={TEXT_LABELS.REMOVE_ITEM_CONFIRM_MESSAGE}
    />
  </SectionCard>
  );
});

export default EditOrderItemsTable;
